import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom, of, Subject, timer } from 'rxjs';
import { catchError, finalize, map, tap, retry, timeout, takeUntil } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { MatchSyncService } from './match-sync.service';
import { LiveDataService } from './livedata.service';
import { SessionService } from './session.service';
import { Match } from './models/match.model';

type MatchDetailState = 'loading' | 'loaded' | 'failed';

@Injectable({ providedIn: 'root' })
export class MatchDataService {
  private loadRequestVersion = 0;
  private snapshotVersion = 0;
  private reloadAfterLoad = false;
  private activeLoad: Promise<void> | null = null;
  private readonly cancelLoad = new Subject<void>();
  private remoteFeedSuspendedForTesting = false;
  private readonly matchDetailStates = signal<ReadonlyMap<number, MatchDetailState>>(new Map());
  private readonly matchDetailRequests = new Map<number, Promise<boolean>>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly http = inject(HttpClient);
  private readonly matchSyncService = inject(MatchSyncService);
  private readonly liveDataService = inject(LiveDataService);
  private readonly sessionService = inject(SessionService);

  constructor() {
    this.liveDataService.connectionRestored.pipe(takeUntilDestroyed()).subscribe(() => {
      if (this.activeLoad) {
        this.reloadAfterLoad = true;
      } else {
        void this.loadMatches();
      }
    });
  }

  loadMatches(): Promise<void> {
    if (this.remoteFeedSuspendedForTesting) {
      return Promise.resolve();
    }
    if (this.activeLoad) {
      return this.activeLoad;
    }
    const requestVersion = ++this.loadRequestVersion;
    this.sessionService.matchesLoadFailed.set(false);
    this.sessionService.setMatchesLoading(this.sessionService.matches().size === 0);
    const request$ = this.http.get<Match[]>(`${environment.apiUrl}/Api/MatchDataReducedBetTypes`).pipe(
      timeout(15000),
      map(matches => {
        if (!Array.isArray(matches) || matches.length === 0) {
          throw new Error('Match offer is not ready');
        }
        return matches;
      }),
      retry({ count: 5, delay: (_error, attempt) => timer(Math.min(2000 * 2 ** (attempt - 1), 30000)) }),
      takeUntil(this.cancelLoad),
      takeUntilDestroyed(this.destroyRef),
      tap(matches => {
        if (this.shouldIgnoreRequest(requestVersion)) {
          return;
        }
        const selectedSports = this.sessionService.selectedSports();
        this.snapshotVersion += 1;
        this.matchDetailRequests.clear();
        this.sessionService.resetLiveData();
        this.matchDetailStates.set(new Map());
        this.matchSyncService.applyMatches(matches);
        this.sessionService.selectedSports.set(new Set(
          [...selectedSports].filter(id => this.sessionService.sports().has(id))
        ));
      }),
      catchError(error => {
        if (!this.shouldIgnoreRequest(requestVersion)) {
          console.error('Failed to load reduced match data', error);
          this.sessionService.matchesLoadFailed.set(true);
        }
        return of([] as Match[]);
      }),
      finalize(() => {
        if (!this.shouldIgnoreRequest(requestVersion)) {
          this.sessionService.setMatchesLoading(false);
          this.liveDataService.connect();
        }
      })
    );

    this.activeLoad = firstValueFrom(request$, { defaultValue: [] }).then(() => undefined)
      .finally(() => {
        this.activeLoad = null;
        if (this.reloadAfterLoad) {
          this.reloadAfterLoad = false;
          void this.loadMatches();
        }
      });
    return this.activeLoad;
  }

  loadMatchDetails(matchId: number): Promise<boolean> {
    if (this.hasMatchDetails(matchId)) {
      return Promise.resolve(true);
    }

    const activeRequest = this.matchDetailRequests.get(matchId);
    if (activeRequest) {
      return activeRequest;
    }

    if (!this.sessionService.matches().has(matchId)) {
      return Promise.resolve(false);
    }

    const snapshotVersion = this.snapshotVersion;
    this.setMatchDetailState(matchId, 'loading');
    const request = firstValueFrom(
      this.http.get<unknown>(`${environment.apiUrl}/Api/MatchDataSingleMatch`, {
        params: { id: matchId }
      }).pipe(
        map(payload => this.extractSingleMatch(payload)),
        tap(match => {
          if (snapshotVersion !== this.snapshotVersion) {
            return;
          }
          if (!match || match.Id !== matchId) {
            throw new Error(`Single match response did not contain match ${matchId}`);
          }

          this.matchSyncService.applyMatches([match]);
          this.setMatchDetailState(matchId, 'loaded');
        }),
        map(() => snapshotVersion === this.snapshotVersion),
        catchError(error => {
          console.error(`Failed to load match ${matchId}`, error);
          if (snapshotVersion === this.snapshotVersion) {
            this.setMatchDetailState(matchId, 'failed');
          }
          return of(false);
        })
      )
    ).finally(() => {
      if (this.matchDetailRequests.get(matchId) === request) {
        this.matchDetailRequests.delete(matchId);
      }
    });

    this.matchDetailRequests.set(matchId, request);
    return request;
  }

  hasMatchDetails(matchId: number): boolean {
    return this.matchDetailStates().get(matchId) === 'loaded';
  }

  isMatchDetailsLoading(matchId: number): boolean {
    return this.matchDetailStates().get(matchId) === 'loading';
  }

  hasMatchDetailsFailed(matchId: number): boolean {
    return this.matchDetailStates().get(matchId) === 'failed';
  }

  suspendRemoteFeedForTesting(): void {
    this.remoteFeedSuspendedForTesting = true;
    this.loadRequestVersion += 1;
    this.cancelLoad.next();
    this.sessionService.matchesLoadFailed.set(false);
    this.liveDataService.disconnect();
    this.sessionService.setMatchesLoading(false);
  }

  markMatchDetailsLoadedForTesting(matchIds: readonly number[]): void {
    for (const matchId of matchIds) {
      this.setMatchDetailState(matchId, 'loaded');
    }
  }

  markMatchDetailsReducedForTesting(matchIds: readonly number[]): void {
    this.matchDetailStates.update(current => {
      const next = new Map(current);
      for (const matchId of matchIds) {
        next.delete(matchId);
      }
      return next;
    });
  }

  private extractSingleMatch(payload: unknown): Match | null {
    const data = isRecord(payload) ? payload['Data'] : null;
    const candidate = Array.isArray(payload)
      ? payload[0]
      : Array.isArray(data)
        ? data[0]
        : data ?? payload;

    return isMatchPayload(candidate) ? candidate : null;
  }

  private setMatchDetailState(matchId: number, state: MatchDetailState): void {
    this.matchDetailStates.update(current => {
      const next = new Map(current);
      next.set(matchId, state);
      return next;
    });
  }

  private shouldIgnoreRequest(requestVersion: number): boolean {
    return this.remoteFeedSuspendedForTesting || requestVersion !== this.loadRequestVersion;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isMatchPayload(value: unknown): value is Match {
  return isRecord(value)
    && typeof value['Id'] === 'number'
    && Array.isArray(value['BetTypes'])
    && typeof value['BetTypeCount'] === 'number';
}
